import React from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { VenuePlan } from '../../graphql/API';
import { useAdminGetDashboardModelsStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardModelsStatsDataQuery';
import { useAdminGetDashboardReservationsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardReservationsDataQuery';
import { useAdminGetDashboardVenuesStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardVenuesStatsDataQuery';
import { calculatePercentageChange } from '../../utils/dashboard';
import { VENUE_MANUAL_TAGS } from '../../utils/manual-tags';
import ActiveCard from './ActiveCard';
import { PackageCard } from './PackageCard';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  marketID: string | null;
  selectedLabel: string;
};

export const Totals: React.FC<Props> = ({
  fromDate,
  priorFrom,
  priorTo,
  toDate,
  marketID,
  selectedLabel,
}) => {
  const isMobileScreen = useMediaQuery('(max-width:486px)');

  const { data: reservationsData, isFetching: reservationDataIsFetching } =
    useAdminGetDashboardReservationsDataQuery(
      {
        fromDate: fromDate || '',
        toDate: toDate || '',
        priorFromDate: priorFrom || '',
        priorToDate: priorTo || '',
        marketID: marketID,
      },
      isMobileScreen,
    );

  const { data, isFetching } = useAdminGetDashboardVenuesStatsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
  });
  const { data: modelsStatsData, isFetching: modelsStatsIsFetching } =
    useAdminGetDashboardModelsStatsDataQuery({
      fromDate: fromDate || '',
      toDate: toDate || '',
      priorFromDate: priorFrom || '',
      priorToDate: priorTo || '',
      marketID: marketID || '',
    });

  const totalUniqueReservationPercentage = calculatePercentageChange(
    reservationsData?.totalUniqueUserReservationsCurrent || 0,
    reservationsData?.totalUniqueUserReservationsPrior || 0,
  );

  const totalReservations = React.useMemo(
    () =>
      (reservationsData?.total1StoryReservationsCurrent || 0) +
      (reservationsData?.total3StoriesReservationsCurrent || 0) +
      (reservationsData?.totalFeedReservationsCurrent || 0) +
      (reservationsData?.totalReelsReservationsCurrent || 0),
    [reservationsData],
  );

  const totalPriorReservations = React.useMemo(
    () =>
      (reservationsData?.total1StoryReservationsPrior || 0) +
      (reservationsData?.total3StoriesReservationsPrior || 0) +
      (reservationsData?.totalFeedReservationsPrior || 0) +
      (reservationsData?.totalFeedReservationsPrior || 0),
    [reservationsData],
  );

  const { totalPercentageReservations } = React.useMemo(() => {
    const totalPercentageReservations = calculatePercentageChange(
      totalReservations,
      totalPriorReservations,
    );

    return {
      totalPercentageReservations,
    };
  }, [reservationsData]);

  const totalCurrentActiveUsersByPeriod = React.useMemo(
    () =>
      (modelsStatsData?.totalPremiumUsers || 0) -
      (modelsStatsData?.totalCurrentActivePremiumUsers || 0) +
      (modelsStatsData?.totalCurrentExpiredPremiumUsers || 0),
    [modelsStatsData],
  );

  const totalPriorActiveUsersByPeriod = React.useMemo(
    () =>
      (modelsStatsData?.totalPremiumUsers || 0) -
      (modelsStatsData?.totalPriorActivePremiumUsers || 0) +
      (modelsStatsData?.totalPriorExpiredPremiumUsers || 0),
    [modelsStatsData],
  );

  return (
    <>
      {isMobileScreen ? (
        <>
          <AllStats>
            <Heading>Dashboard</Heading>
          </AllStats>
          <ActiveCard
            bgColor="#FDF1E6"
            count={String(modelsStatsData?.totalActiveModelsLastMonth)}
            label="Active models"
            link="/users"
          />
          <PackageContainer>
            <PackageCard
              isMobile
              bgColor="#FDF1E6"
              count={String(modelsStatsData?.totalPremiumUsers)}
              label="Premium models"
            />
            <PackageCard
              isMobile
              bgColor="#FDF1E6"
              count={String(modelsStatsData?.totalCredits)}
              label="Credits purchased"
            />
          </PackageContainer>
          <ActiveCard
            bgColor="#E6EAFD"
            count={String(data?.totalActiveVenues)}
            label="Active venues"
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}`}
          />
          <PackageContainer>
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.free}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalFreeVenuesWithoutOffers)}
              label="Free"
            />
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalStarterVenuesWithoutOffers)}
              label="Starter"
              substr={String(data?.totalStarterFreeVenues)}
            />
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalPlusVenuesWithoutOffers)}
              label="Plus"
              substr={String(data?.totalPlusFreeVenues)}
            />
          </PackageContainer>

          <ActiveCard
            bgColor="#E6EAFD"
            count={String(data?.totalActiveBrands)}
            label="Active brands subscribed"
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}`}
          />
          <PackageContainer>
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.basic}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalBasicBrandsWithoutOffers)}
              label="Basic"
              substr={String(data?.totalBasicFreeBrands)}
            />
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalStarterBrandsWithoutOffers)}
              label="Starter"
              substr={String(data?.totalStarterFreeBrands)}
            />
            <PackageCard
              link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`}
              isMobile
              bgColor="#FDF1E6"
              count={String(data?.totalPlusBrandsWithoutOffers)}
              label="Plus"
              substr={String(data?.totalPlusFreeBrands)}
            />
          </PackageContainer>
          <AllStats>
            <Heading>All Stats</Heading>
            <Time>{selectedLabel}</Time>
          </AllStats>
          <StatCard
            name="Reservations"
            count={String(totalReservations)}
            compare={`${totalPercentageReservations}%`}
            percentageNumber={totalPercentageReservations}
          />
          <StatCard
            isFetching={reservationDataIsFetching}
            name="Unique users making a reservation"
            count={String(reservationsData?.totalUniqueUserReservationsCurrent || 0)}
            compare={`${totalUniqueReservationPercentage}%`}
            isNegative={totalUniqueReservationPercentage < 0}
          />
        </>
      ) : (
        <>
          <PackageContainerDesktop>
            {(modelsStatsIsFetching || isFetching) && (
              <>
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
              </>
            )}
            {!isFetching && modelsStatsData && (
              <>
                <ActiveCard
                  showComparison
                  bgColor="#FDF1E6"
                  count={String(modelsStatsData?.totalActiveModelsLastMonth)}
                  label="Active models"
                  link="/users"
                  selectedLabel={selectedLabel}
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalActiveModelsCurrentMonth || 0,
                    modelsStatsData?.totalActiveModelsPriorMonth || 0,
                  )}
                />
                <PackageCard
                  link="/users?premium=true"
                  showFooter
                  showComparison
                  selectedLabel={selectedLabel}
                  bgColor="#FDF1E6"
                  count={String(modelsStatsData?.totalPremiumUsers)}
                  label="Premium models"
                  newCount={modelsStatsData?.totalNewPremiumUsers || 0}
                  canceledCount={modelsStatsData?.totalPremiumExpiredUsers || 0}
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalCurrentPremiumUsers || 0,
                    modelsStatsData?.totalPriorPremiumUsers || 0,
                  )}
                />
                <PackageCard
                  showComparison
                  selectedLabel={selectedLabel}
                  bgColor="#FDF1E6"
                  count={String(modelsStatsData?.totalCredits)}
                  label="Credits purchased"
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalCurrentCredits || 0,
                    modelsStatsData?.totalCredits || 0,
                  )}
                />
              </>
            )}
          </PackageContainerDesktop>
        </>
      )}
    </>
  );
};

const PackageContainerDesktop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
`;

const PackageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 15px 0px 48px;
`;

const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
